import {CustomDataTable, OptionMenu, PageOptions} from "../common";
import SearchIcon from "@mui/icons-material/Search";
import AddIcon from "@mui/icons-material/Add";
import {VLoyaltyContainer} from "../../pages/VLoyalty/VLoyaltyContainer.tsx";
import React, {useCallback, useEffect, useState} from "react";
import {Link, useNavigate} from "react-router-dom";
import {v2ReactURL} from "../../utils/constant.ts";
import {axiosFunction, toastMessage} from "../commonFunctions";
import {DropdownMenu, DropdownToggle, UncontrolledDropdown} from "reactstrap";
import {TfiMoreAlt} from "react-icons/tfi";
import {
    RowData,
    responseSchema
} from "../../hooks/VLoyalty/LoyaltyRelatedApiZodSchema.ts"
import {Column} from "../common/CustomDataTable/CustomDataTable.tsx";
import {debounce, MenuItem} from "@mui/material";
import {CiEdit, CiTrash} from "react-icons/ci";
 
import {apiResponseHandler} from "../../utils/functions/apiResponseHandler.ts";
import {deleteCampaign} from "../commonFunctions/VLoyalty/deleteCampaign.ts";
import {useSelector} from "react-redux";
import { ErrorState, Pagination } from "../../pages/Contact/MainDataTable.tsx";

interface Permissions {
    isView: number;
    isEdit?: number;
    isDelete?: number;
    isInsert?: number;
}

interface CampaignTabProps {
    permissions: Permissions;
}

const CampaignTab: React.FC<CampaignTabProps> = ({permissions}) => {
    const navigate = useNavigate();
    const decimalPoint = useSelector((state: any) => state.sessionData.sessionScope.decimalPoint);
    const [pageOption, setPageOption] = useState<string>('15');
    const [searchValue, setSearchValue] = useState<string>('');
    const [loadingData, setLoadingData] = useState<boolean>(false);
    const [openRowMenuId, setOpenRowMenuId] = useState<number | null>(null);
    const [sortColumn, setSortColumn] = useState<string>('');
    const [sortDirection, setSortDirection] = useState<string>('');
    const [pagination, setPagination] = useState<Pagination>({
        page: 0,
        pages: 0,
        perpage: 0,
        total: 0,
    });
    const [rows, setRows] = useState<RowData[]>([]);


    const fetchCampaignDatatable = async (
        searchValue: string,
        startIndex: number,
        pageLength: number,
        sortColumn: string,
        sortDirection: string,
    ) => {
        setLoadingData(true);
        const payloadData = {
            searchValue: searchValue,
            length: pageLength,
            start: startIndex,
            draw: 1,
            sortColumn: sortColumn,
            sortDirection: sortDirection
        };

        const res = await axiosFunction(
            `${v2ReactURL}/loyalty/campaign/datatable`,
            "POST",
            payloadData
        );
        const error: ErrorState | null = apiResponseHandler(res, true);
        if (error != null) {
            toastMessage(error.message);
            setLoadingData(false);
            return;
        }

        const validatedData = responseSchema.safeParse(res.data.response);
        if (validatedData.success) {
            const contactList = validatedData.data?.data?.map((col: any, index: number) => ({
                ...col,
                index: index + 1,
                checked: false,
            }));
            setPagination(validatedData.data?.dataTableMetaDTO);
            setRows(contactList);
        } else {
            console.error("API response validation failed:", validatedData.error);
        }
        setLoadingData(false);
    };

    const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
        fetchCampaignDatatable(searchValue, (page - 1) * parseInt(pageOption), parseInt(pageOption), sortColumn, sortDirection);
    };

    const handleChangePageOption = (e: { target: { value: any; }; }) => {
        const selectedValue = e.target.value;
        setPageOption(parseInt(selectedValue).toString());
        fetchCampaignDatatable(searchValue, 0, parseInt(selectedValue), sortColumn, sortDirection);
    };

    const handleSearchData = useCallback(
        debounce((searchValue: string) => {
            fetchCampaignDatatable(searchValue, pagination.page * parseInt(pageOption), parseInt(pageOption), sortColumn, sortDirection);
        }, 500),
        [pagination]
    );


    function handleEditCustomer(loyaltyCampaignId: number): void {
        navigate(`/loyalty/campaign/${loyaltyCampaignId}/edit`);
    }

    const handleDeleteCampaign = async (loyaltyCampaignId: number) => {
        let boolean = await deleteCampaign(loyaltyCampaignId);
        if (boolean) {
            fetchCampaignDatatable(searchValue, pagination.page * parseInt(pageOption), parseInt(pageOption), "", "");
        }
    }


    const actionMenuItems = (row: RowData) => {
        return <>
            {Boolean(permissions.isEdit) && (
                <MenuItem onClick={() => handleEditCustomer(row.loyaltyCampaignId)}>
                    <CiEdit className="common__icon"/>
                    Edit
                </MenuItem>
            )}
            {Boolean(permissions.isDelete) && (
                <MenuItem onClick={() => handleDeleteCampaign(row.loyaltyCampaignId)}>
                    <CiTrash className="common__icon"/>
                    Delete
                </MenuItem>
            )}
        </>
    };
    const handleRowMenuOpen = (event: React.MouseEvent<HTMLElement>, rowId: number) => {
        event.stopPropagation();
        setOpenRowMenuId(openRowMenuId == rowId ? null : rowId);
    };

    const toggleSorting = (column: Column) => {
        setSortColumn((prevColumn) => {
            if (prevColumn === column.field) {
                setSortDirection((prevDirection) => (prevDirection === "ASC" ? "DESC" : "ASC"));
                return prevColumn;
            } else {
                setSortDirection("ASC");
                return column.field;
            }
        });
    };

    useEffect(() => {
        fetchCampaignDatatable(searchValue, pagination.page * parseInt(pageOption), parseInt(pageOption), sortColumn, sortDirection);
    }, [sortColumn, sortDirection]);

    const campaignColumns = [
        {
            field: "srNo",
            headerName: "Sr. No",
            width: 70,
            defaultChecked: true,
            renderCell: (params: any) =>
                pagination.page * pagination.perpage + params.row.index,
        },
        {
            field: "name",
            headerName: "Campaign Name",
            renderCell: (params: any) => {
                return (
                    <Link to={`/loyalty/campaign/${params.row.loyaltyCampaignId}`}>
                        {params.row.name ? params.row.name : "-"}
                    </Link>
                );
            },
            width: 200,
            defaultChecked: true,
            sorted: true,
        },
        {
            field: "type",
            headerName: "Campaign Type",
            width: 150,
            defaultChecked: true,
        },
        {
            field: "points",
            headerName: "Redemption Point",
            width: 150,
            defaultChecked: true,
            sorted: true,
            renderCell: (params:any) => params.row.points.toFixed(decimalPoint || 2)
        },
        {
            field: "action",
            headerName: "Action",
            width: 10,
            defaultChecked: true,
            renderCell: (params: any) => (
                <div className="d-flex justify-content-center">
                    {(
                        <UncontrolledDropdown>
                            <DropdownToggle
                                tag="a"
                                onClick={(event) =>
                                    handleRowMenuOpen(event, params.row.loyaltyCammpaignId)
                                }
                                className="btn btn-trigger action-btn dropdown-toggle btn-icon me-n1"
                            >
                                <TfiMoreAlt/>
                            </DropdownToggle>

                            <DropdownMenu style={{minWidth: "8rem"}}>
                                <OptionMenu menuItems={actionMenuItems(params.row)}/>
                            </DropdownMenu>
                        </UncontrolledDropdown>
                    )}
                </div>
            ),
        },
    ];

    return (
        <>
            <VLoyaltyContainer>
                <div className="custom-toolbar">
                    <div className="left-toolbar">
                        <div className="pageBtn">
                            <PageOptions
                                pageOption={pageOption}
                                handleChangePageOption={handleChangePageOption}
                            />
                        </div>
                    </div>
                    <div className="search-toolbar">
                        <div className="search-bar ml-3">
                            <input
                                type="text"
                                placeholder="Search List"
                                className="search-input"
                                value={searchValue}
                                onChange={(e) => {
                                    const value = e && e.target.value;
                                    setSearchValue(value);
                                    handleSearchData(value);
                                }}
                            />
                            <SearchIcon className="search-icon"/>
                        </div>
                        {permissions.isInsert ? (
                            <div className="create-btn-container">
                                <button
                                    onClick={() => navigate("/loyalty/campaign/new")}
                                    className="create-btn"
                                >
                                    <AddIcon className="add-mui"/>
                                </button>
                            </div>
                        ) : (
                            <></>
                        )}
                    </div>
                </div>
            </VLoyaltyContainer>

            <CustomDataTable
                rows={rows}
                FixedTable={true}
                columns={campaignColumns}
                paginationInfo={pagination}
                handlePageChange={handlePageChange}
                pageOption={pageOption}
                handleChangePageOption={handleChangePageOption}
                loading={loadingData}
                isCheckboxShow={false}
                toggleSorting={toggleSorting}
                defaultChecker={(_: Column): boolean => true}
            />
        </>
    );
};

export default CampaignTab;
